/**
 * V8 — SINGLE_PLAYBACK_OWNER invariant for a given video asset identity.
 *
 * Same asset, same moment: exactly one owner in
 *   NONE | CANVAS_HOVER | CANVAS_EXPLICIT | MODAL
 *
 * Event-driven (subscribe/notify). No polling, RAF, or timers.
 * Never pauses videos via DOM querySelectorAll — only the owner that holds
 * the token is told to release via its subscriber callback.
 */

export type PlaybackOwnerKind = 'NONE' | 'CANVAS_HOVER' | 'CANVAS_EXPLICIT' | 'MODAL'

export type PlaybackOwnerChange = {
  assetId: string
  owner: PlaybackOwnerKind
  token: symbol | null
  currentTime: number
}

type Entry = {
  owner: PlaybackOwnerKind
  token: symbol | null
  currentTime: number
  listeners: Set<(change: PlaybackOwnerChange) => void>
}

const entries = new Map<string, Entry>()

function ensure(assetId: string): Entry {
  let e = entries.get(assetId)
  if (!e) {
    e = { owner: 'NONE', token: null, currentTime: 0, listeners: new Set() }
    entries.set(assetId, e)
  }
  return e
}

function notify(assetId: string, e: Entry) {
  const change: PlaybackOwnerChange = {
    assetId,
    owner: e.owner,
    token: e.token,
    currentTime: e.currentTime,
  }
  for (const fn of e.listeners) fn(change)
}

/** Claim ownership. Previous owner (if any) is notified with the new owner. */
export function claimPlayback(
  assetId: string,
  owner: Exclude<PlaybackOwnerKind, 'NONE'>,
  currentTime = 0,
): symbol {
  const e = ensure(assetId)
  const token = Symbol(`${owner}:${assetId}`)
  e.owner = owner
  e.token = token
  if (Number.isFinite(currentTime) && currentTime > 0) e.currentTime = currentTime
  notify(assetId, e)
  return token
}

/** Release only if this token still owns the asset. */
export function releasePlayback(assetId: string, token: symbol | null | undefined): void {
  if (!token) return
  const e = entries.get(assetId)
  if (!e || e.token !== token) return
  e.owner = 'NONE'
  e.token = null
  notify(assetId, e)
}

export function publishPlaybackTime(assetId: string, currentTime: number): void {
  const e = ensure(assetId)
  if (Number.isFinite(currentTime)) e.currentTime = Math.max(0, currentTime)
}

export function getPlaybackOwner(assetId: string): PlaybackOwnerKind {
  return entries.get(assetId)?.owner ?? 'NONE'
}

export function getPlaybackTime(assetId: string): number {
  return entries.get(assetId)?.currentTime ?? 0
}

export function getPlaybackToken(assetId: string): symbol | null {
  return entries.get(assetId)?.token ?? null
}

export function subscribePlayback(assetId: string, listener: (change: PlaybackOwnerChange) => void): () => void {
  const e = ensure(assetId)
  e.listeners.add(listener)
  return () => {
    e.listeners.delete(listener)
  }
}

/** Test helper — clear all ownership (node-tests only). */
export function __resetPlaybackCoordinatorForTests(): void {
  entries.clear()
}

if (typeof window !== 'undefined') {
  ;(window as unknown as { __pcvVideoPlaybackCoordinator?: object }).__pcvVideoPlaybackCoordinator = {
    getPlaybackOwner,
    getPlaybackTime,
    claimPlayback,
    releasePlayback,
  }
}
